import React, { useState, useRef } from 'react';
import { usePageTitle } from "../hooks/usePageTitle";

const AvatarTest: React.FC = () => {
  usePageTitle("Avatar Test");

  // Avatar modal state
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState<{username: string, avatarUrl: string, balance: number, totalMined: number, distance: number, rank: number} | null>(null);

  // Avatar mapping function (same as ledger)
  const getAvatarForUser = (username: string): string => {
    const avatarMap: { [key: string]: string } = {
      'madminer': '/avatars/madminer.png',
      'job': '/avatars/job.png',
      'kat': '/avatars/kat.png',
      'melathon': '/avatars/melathon.png',
      'scar': '/avatars/scar.png',
      'willy_wingo': '/avatars/willy_wingo.png',
      'erock': '/avatars/erock.png',
      'jimmy': '/avatars/jimmy.png',
      'kirkland': '/avatars/kirkland.png',
      'joni': '/avatars/joni.png',
      'coach_2': '/avatars/coach_2.png',
      'pr_tek': '/avatars/pr_tek.png',
      'taytay': '/avatars/taytay.png',
      'sam_spade': '/avatars/sam_spade.png',
      'ernest_j': '/avatars/ernest_j.png',
      'trimbawolf': '/avatars/trimbawolf.png',
      'barry': '/avatars/barry.png',
      'wheelz': '/avatars/wheelz.png',
      'vienna': '/avatars/vienna.png',
      'fig': '/avatars/fig.jpeg',
      'chungus': '/avatars/chungus.png',
    };
    // Normalize username for avatar lookup
    let normalized = username.trim().toLowerCase();
    normalized = normalized.replace(/\s+/g, '_'); // spaces to underscores
    normalized = normalized.replace(/[^a-z0-9_\-]/g, ''); // remove all but a-z, 0-9, _, -
    return avatarMap[normalized] || '/avatars/default.png';
  };

  const testUsers = [
    { username: 'Ernest J.', displayName: 'Ernest J.', balance: 150, totalMined: 300, distance: 96, rank: 5 },
    { username: 'ernest_j', displayName: 'ernest_j', balance: 150, totalMined: 300, distance: 96, rank: 5 },
    { username: 'TrimbaWolf', displayName: 'TrimbaWolf', balance: 200, totalMined: 400, distance: 128, rank: 3 },
    { username: 'trimbawolf', displayName: 'trimbawolf', balance: 200, totalMined: 400, distance: 128, rank: 3 },
    { username: 'Barry', displayName: 'Barry', balance: 180, totalMined: 360, distance: 115, rank: 4 },
    { username: 'barry', displayName: 'barry', balance: 180, totalMined: 360, distance: 115, rank: 4 },
    { username: 'Coach 2', displayName: 'Coach 2', balance: 100, totalMined: 200, distance: 64, rank: 8 },
    { username: 'coach_2', displayName: 'coach_2', balance: 100, totalMined: 200, distance: 64, rank: 8 },
    { username: 'KAT', displayName: 'KAT', balance: 250, totalMined: 500, distance: 160, rank: 2 },
    { username: 'kat', displayName: 'kat', balance: 250, totalMined: 500, distance: 160, rank: 2 },
  ];

  // Handle avatar click (same as ledger)
  const handleAvatarClick = (user: any) => {
    console.log('Avatar clicked for user:', user.username);
    const avatarUrl = getAvatarForUser(user.username);
    setSelectedAvatar({ 
      username: user.username, 
      avatarUrl, 
      balance: user.balance, 
      totalMined: user.totalMined, 
      distance: user.distance, 
      rank: user.rank
    });
    setShowAvatarModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            <span className="inline-flex items-baseline">
              <span className="text-[#E6C200] font-bold">W</span>
              <span>INGO</span>
            </span> Avatar Test
          </h1>
          <p className="text-gray-600">Testing how new avatars look in ledger format</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
            <h2 className="text-lg font-semibold text-gray-900">Avatar Preview</h2>
            <p className="text-sm text-gray-600">Testing different username formats and normalization</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 font-mono">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Username in Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Display Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avatar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Avatar URL
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Normalized Key
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                                 {testUsers.map((user, index) => {
                   const avatarUrl = getAvatarForUser(user.username);
                   const normalized = user.username.trim().toLowerCase()
                     .replace(/\s+/g, '_')
                     .replace(/[^a-z0-9_\-]/g, '');
                   
                   // Avatar positioning logic (same as ledger)
                   let avatarY = '2';
                   let avatarHeight = '44';
                   if (normalized === 'pr_tek') {
                     avatarY = '1.5';
                     avatarHeight = '51';
                   } else if (user.username === 'Coach_2') {
                     avatarY = '-1.5';
                     avatarHeight = '60';
                   } else if (normalized === 'joni') {
                     avatarY = '-7';
                     avatarHeight = '60';
                                               } else if (normalized === 'trimbawolf') {
          avatarY = '1.9';
          avatarHeight = '60';
        } else if (normalized === 'barry') {
          avatarY = '1.9';
          avatarHeight = '60';
        }
                   
                   return (
                     <tr key={index} className="hover:bg-gray-50">
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                         {user.username}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                         {user.displayName}
                       </td>
                       <td className="px-6 py-4 whitespace-nowrap">
                         <div className="flex justify-center">
                           <div 
                             className="flex-shrink-0 cursor-pointer hover:scale-110 transition-transform duration-200" 
                             style={{ width: '2.5rem', height: '3rem' }}
                             onClick={() => handleAvatarClick(user)}
                           >
                            <svg
                              width="100%"
                              height="100%"
                              viewBox="0 0 40 48"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              style={{ display: 'block' }}
                            >
                              <defs>
                                <clipPath id={`avatar-clip-test-${index}`}>
                                  <path d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z" />
                                </clipPath>
                                <linearGradient id={`border-gradient-test-${index}`} x1="0%" y1="100%" x2="0%" y2="0%">
                                  <stop offset="0%" stopColor="#B02E0C" />
                                  <stop offset="25%" stopColor="#FF7A00" />
                                  <stop offset="50%" stopColor="#FFA733" />
                                  <stop offset="75%" stopColor="#FF7A00" />
                                  <stop offset="100%" stopColor="#B02E0C" />
                                </linearGradient>
                              </defs>
                              <path
                                d="M 2 16 A 14 14 0 0 1 16 2 L 24 2 A 14 14 0 0 1 38 16 L 38 32 A 14 14 0 0 1 24 46 L 16 46 A 14 14 0 0 1 2 32 Z"
                                stroke={`url(#border-gradient-test-${index})`}
                                strokeWidth="3"
                                fill="white"
                              />
                                                             <image
                                 href={avatarUrl}
                                 x="2"
                                 y={avatarY}
                                 width="36"
                                 height={avatarHeight}
                                 clipPath={`url(#avatar-clip-test-${index})`}
                                 preserveAspectRatio="xMidYMid slice"
                               />
                            </svg>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 font-mono">
                        {avatarUrl}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 font-mono">
                        {normalized}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Test Notes</h3>
          <ul className="text-sm text-gray-600 space-y-2">
            <li>• <strong>Ernest J.</strong> should display as "Ernest J." and use ernest_j.png avatar</li>
            <li>• <strong>TrimbaWolf</strong> should display as "TrimbaWolf" and use trimbawolf.png avatar</li>
            <li>• <strong>Coach 2</strong> should display as "Coach 2" and use coach_2.png avatar</li>
            <li>• <strong>KAT</strong> should display as "KAT" and use kat.png avatar</li>
            <li>• The normalization converts spaces to underscores and removes special characters</li>
                     </ul>
         </div>
       </div>

       {/* Avatar Modal */}
       {showAvatarModal && selectedAvatar && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="relative w-full max-w-sm perspective-1000">
             <div className="relative w-full h-96 transform-style-preserve-3d transition-transform duration-700" id="avatarCard">
               {/* Front Side */}
               <div className="absolute inset-0 w-full h-full backface-hidden">
                 <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full overflow-hidden">
                   {/* Avatar Image with Username Overlay */}
                   <div className="relative">
                     <img
                       src={selectedAvatar.avatarUrl}
                       alt={`${selectedAvatar.username}'s avatar`}
                       className="w-full h-96 object-cover"
                                               style={{
                          objectPosition:
                            selectedAvatar.username.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '') === 'pr_tek'
                              ? 'center 10%'
                              : selectedAvatar.username === 'Coach_2'
                              ? 'center 20%'
                                                              : selectedAvatar.username.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '') === 'trimbawolf'
                                ? 'center 10%'
                                : selectedAvatar.username.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_\-]/g, '') === 'barry'
                                ? 'center 2%'
                                : 'center 25%'
                        }}
                       onError={(e) => {
                         const target = e.target as HTMLImageElement;
                         target.src = '/avatars/default.png';
                       }}
                     />
                     {/* Username Overlay */}
                     <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none"></div>
                     <div className="absolute bottom-4 left-4 right-4">
                       <h3 className="text-2xl font-bold text-white drop-shadow-lg">
                         {selectedAvatar.username}
                       </h3>
                     </div>
                     {/* Close Button */}
                     <button
                       onClick={() => setShowAvatarModal(false)}
                       className="absolute top-4 right-4 text-white hover:text-gray-300 text-2xl font-bold bg-black/30 rounded-full w-8 h-8 flex items-center justify-center backdrop-blur-sm z-20"
                     >
                       <span className="-mt-0.5">×</span>
                     </button>
                   </div>
                   {/* Stats Section */}
                   <div className="p-2">
                     <div className="bg-gray-50 rounded-lg p-2 flex flex-row items-center justify-start w-full">
                       {/* WINGO Balance with Rank */}
                       <div className="flex items-center space-x-2">
                         <div className="w-8 h-8 bg-gradient-to-br from-[#E6C200] to-[#FFD700] rounded-full flex items-center justify-center">
                           <span className="text-white font-bold text-sm">W</span>
                         </div>
                         <div>
                           <div className="text-xs text-gray-500 font-medium">Balance</div>
                           <div className="text-lg font-bold text-gray-900">
                             <span className="sm:hidden flex items-center">
                               {selectedAvatar.balance.toLocaleString()}
                               <span className="mx-1 text-gray-400">&middot;</span>
                               <span className="text-lg font-bold text-gray-900 align-middle">#{selectedAvatar.rank}</span>
                             </span>
                             <span className="hidden sm:inline">
                               {selectedAvatar.balance.toLocaleString()}
                               <span className="text-xs text-gray-500 font-normal ml-1">(Rank: {selectedAvatar.rank})</span>
                             </span>
                           </div>
                         </div>
                       </div>
                       {/* Mined Stats */}
                       <div className="text-left ml-6">
                         <div className="text-xs text-gray-500 font-medium">Mined</div>
                         <div className="text-lg font-bold text-gray-900">
                           {selectedAvatar.totalMined.toLocaleString()} <span className="text-xs text-gray-500 font-normal">
                             <span className="sm:hidden">
                               ({selectedAvatar.distance >= 10
                                 ? Math.round(selectedAvatar.distance)
                                 : selectedAvatar.distance.toFixed(1)
                               } km)
                             </span>
                             <span className="hidden sm:inline">
                               ({selectedAvatar.distance.toFixed(1)} km)
                             </span>
                           </span>
                         </div>
                       </div>
                     </div>
                   </div>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default AvatarTest;
